import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search, Star } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/i18n/LangContext";
import { certificates, type Certificate } from "@/data/certificates";
import { certificatePathFor } from "@/data/certificateSlugs";
import { getBookingAction } from "@/data/certificateBookingActions";
import { LazyBookingDialog } from "@/components/site/LazyBookingDialog";
import { CategoryBadge } from "@/components/site/CategoryBadge";
import { ModeBadges } from "@/components/site/ModeBadge";
import { RotatingWord } from "./RotatingWord";
import { PageHeroCard } from "./PageHeroCard";
import googleG from "@/assets/google-g.svg";
import { GOOGLE_REVIEWS_URL } from "@/data/googleReviews";

const POPULAR_SLUGS = [
  "hogre-behorighet",
  "alkolas",
  "droger-alkohol",
] as const;

export const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const bookingHref = lang === "sv" ? "/boka-tid" : "/en/book-appointment";
  const [searchQuery, setSearchQuery] = useState("");
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);

  const popularCerts = useMemo(
    () =>
      POPULAR_SLUGS.map((slug) => certificates.find((c) => c.slug === slug)).filter(
        (c): c is Certificate => Boolean(c),
      ),
    [],
  );
  const selectedAction = selectedCert ? getBookingAction(selectedCert.slug) : null;

  const rotatingWords = [
    t.categories.korkort,
    t.categories.alkohol,
    t.categories.sjofolk,
    t.categories.arbete,
    t.categories.resor,
    t.categories.forsakring,
    t.categories.sport,
    t.categories.adoption,
    t.categories.afs,
    t.categories.ovriga,
  ];

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return certificates
      .filter((cert) => {
        const haystack = [cert.title[lang], cert.description[lang], t.categories[cert.category]]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 6);
  }, [lang, searchQuery, t.categories]);

  return (
    <>
      <PageHeroCard
        imageSrc="/hero-clinic.jpg"
        imageAvif="/hero-clinic.avif"
        imageWebp="/hero-clinic.webp"
        imageAlt=""
        priority
        overlay={
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-4 left-4 right-4 z-10 inline-flex items-center justify-center gap-3 rounded-full border border-border/60 bg-white px-4 py-2.5 shadow-card transition-base hover:border-primary/25 sm:bottom-6 sm:left-6 sm:right-auto sm:justify-start lg:bottom-8 lg:left-8"
          >
            <img src={googleG} alt="" width={20} height={20} className="h-5 w-5" />
            <span className="text-left">
              <span className="flex items-center gap-2">
                <span className="font-display text-base font-semibold leading-none text-foreground">
                  {t.hero.stat2}
                </span>
                <span className="flex text-accent">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </span>
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{t.hero.googleReviewsLabel}</span>
            </span>
          </a>
        }
      >
          <div className="animate-fade-up relative z-20 mx-auto max-w-3xl text-center lg:max-w-5xl xl:max-w-6xl">
            <h1 className="text-balance font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
              <span className="block">{t.hero.titlePrefix}</span>
              <span className="block">
                <RotatingWord words={rotatingWords} intervalMs={4200} />
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-4xl text-balance text-base leading-relaxed text-muted-foreground sm:max-w-5xl sm:text-lg lg:max-w-6xl">
              {t.hero.subtitle}
            </p>

            <div className="relative z-30 mx-auto mt-10 max-w-3xl">
              <div className="group flex items-center gap-2 rounded-full border border-border/70 bg-white p-1.5 shadow-[0_8px_30px_-12px_hsl(162_52%_8%/0.28)] ring-1 ring-black/[0.03] transition-base focus-within:border-primary/30 focus-within:shadow-[0_12px_36px_-12px_hsl(162_52%_8%/0.35)] focus-within:ring-primary/15">
                <div className="relative min-w-0 flex-1">
                  <Search
                    className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground transition-base group-focus-within:text-primary"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      const first = searchResults[0];
                      if (!first) return;
                      e.preventDefault();
                      navigate(certificatePathFor(first.slug, lang));
                    }}
                    placeholder={t.hero.searchPlaceholder}
                    aria-label={t.hero.searchAriaLabel}
                    className="h-12 w-full border-0 bg-transparent pl-12 pr-10 text-base shadow-none placeholder:text-muted-foreground/80 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-base [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-base hover:bg-muted hover:text-foreground"
                      aria-label={t.nav.searchClose}
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden />
                    </button>
                  ) : null}
                </div>
                <Button asChild variant="hero" size="lg" className="shrink-0 px-4 sm:px-5">
                  <a href={bookingHref}>
                    {t.hero.bookCta}
                    <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
                  </a>
                </Button>
              </div>

              {searchQuery.trim() ? (
                <div className="absolute inset-x-0 top-[calc(100%+0.6rem)] z-50 overflow-hidden rounded-2xl border border-border/70 bg-white text-left shadow-elevated animate-fade-in">
                  {searchResults.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-muted-foreground">{t.hero.searchNoResults}</p>
                  ) : (
                    <ul className="divide-y divide-border/60 py-1">
                      {searchResults.map((cert) => (
                        <li key={cert.slug}>
                          <Link
                            to={certificatePathFor(cert.slug, lang)}
                            className="flex items-start justify-between gap-3 px-4 py-3 transition-base hover:bg-primary-soft/60"
                            onClick={() => setSearchQuery("")}
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-foreground">{cert.title[lang]}</span>
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                {t.categories[cert.category]} · {cert.price[lang]}
                              </span>
                            </span>
                            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" aria-hidden />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="relative z-0 mt-12 w-full pb-6 sm:pb-4">
            <h2 className="text-center font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {t.hero.popularLabel}
            </h2>
            <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {popularCerts.map((cert) => (
                <article
                  key={cert.slug}
                  className="group relative z-0 flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white p-6 text-left shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
                >
                  <div className="flex items-center justify-between gap-2">
                    <CategoryBadge category={cert.category} link />
                    <ModeBadges modes={cert.modes} />
                  </div>

                  <h3 className="mt-5 font-display text-xl font-semibold leading-tight text-foreground">
                    {cert.title[lang]}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                    {cert.description[lang]}
                  </p>

                  <div className="mt-5 flex items-end justify-between border-t border-border/60 pt-4">
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        {lang === "sv" ? "Pris" : "Price"}
                      </span>
                      <div className="font-display text-lg font-semibold text-foreground">
                        {cert.price[lang]}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild variant="ghost-outline" size="sm">
                        <Link
                          to={certificatePathFor(cert.slug, lang)}
                          aria-label={t.services.readMoreAbout.replace("{title}", cert.title[lang])}
                        >
                          {t.services.readMore}
                        </Link>
                      </Button>
                      <Button
                        variant="hero"
                        size="sm"
                        onClick={() => {
                          setSelectedCert(cert);
                          setBookDialogOpen(true);
                        }}
                      >
                        {t.services.bookCta}
                        <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Button asChild variant="ghost-outline" size="xl" className="w-full sm:w-auto">
                <a href="/lakarintyg">
                  {t.hero.browseCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
      </PageHeroCard>

      <LazyBookingDialog
        open={bookDialogOpen}
        onOpenChange={(open) => {
          setBookDialogOpen(open);
          if (!open) setSelectedCert(null);
        }}
        title={selectedCert?.title[lang] ?? t.services.bookCta}
        action={selectedAction}
        contentKey={selectedCert?.slug}
      />
    </>
  );
};
